/**
 * K9 Vitality — Weight-Based Plan Selector
 *
 * Replaces the size-first variant picker with a dog-weight → plan-duration flow.
 * Maps customer selections to real Shopify variant IDs and quantities.
 *
 * 30-day plans: use native product form + Breeze subscription widget (single variant, qty 1).
 * 60/90-day plans: one-time purchase only, AJAX add-to-cart (supports multi-item), Rebuy cart trigger.
 */

(function () {
  'use strict';

  function initWeightPlanSelector(sectionId) {
    var root = document.getElementById('kv-wps-' + sectionId);
    if (!root || root.dataset.kvWpsInit) return;
    root.dataset.kvWpsInit = '1';

    // --- Read embedded data ---
    var dataEl = document.getElementById('kv-wps-data-' + sectionId);
    if (!dataEl) return;

    var data;
    try { data = JSON.parse(dataEl.textContent); } catch (e) { return; }

    var variantMap        = data.variantMap        || {};
    var priceMap          = data.priceMap          || {};
    var compareAtPriceMap = data.compareAtPriceMap || {};
    var planConfig        = data.planConfig        || {};
    var inventoryMap      = data.inventoryMap      || {};
    var jerkyVariantId    = data.jerkyVariantId    || null;
    var jerkyDiscountCode = data.jerkyDiscountCode || 'K990FREEJERKY';
    var jerkyAvailable    = data.jerkyAvailable === true;
    var productLabel      = data.productLabel      || 'PRO';

    // Per-template offer overrides, published by an inline script earlier in the
    // page (kv-hj90-plan-lock). Read lazily so script load order cannot matter.
    // Defaults reproduce the standing PDP offer: jerky on the 90-day plan only,
    // and no second code.
    function offer()      { return window.KV_WPS_OFFER || {}; }
    function jerkyOnPlan() {
      var plans = offer().jerkyPlans;
      if (!plans || !plans.length) plans = ['90'];
      return plans.map(String).indexOf(String(state.plan)) !== -1;
    }

    // UPI AutoPay per-debit cap (RBI). A household over this needs more than one
    // mandate, and UPI authorisation redirects away from the page, so the second
    // mandate can never be collected reliably. We block subscribe above the cap
    // rather than take a payment we cannot finish. Mirrors MANDATE_CAP_PAISE in the
    // Brain App's subscription-split lib; keep both in sync.
    var MANDATE_CAP_PAISE = 1500000;

    // Daily dosage per weight range (8g scoop)
    var dosageConfig = {
      '4-11':  { text: '\u00bd scoop per day', grams: '4g'  },
      '12-26': { text: '1 scoop per day',      grams: '8g'  },
      '27-45': { text: '2 scoops per day',     grams: '16g' },
      '46+':   { text: '3 scoops per day',     grams: '24g' }
    };

    // Display labels for the 4 weight bands (match Step 1 buttons; en dash in ranges)
    var WEIGHT_LABELS = {
      '4-11':  'Upto 11 kg',
      '12-26': '12\u201326 kg',
      '27-45': '27\u201345 kg',
      '46+':   'Over 46 kg'
    };
    var MAX_DOGS = 6; // total dogs cap (Dog 1 + up to 5 extra)

    // --- State ---
    // Phase 4: Dog 1's band lives in state.weight (Step 1). extraDogs holds dogs 2..N,
    // each with its OWN band. dog1Name is the optional name typed for Dog 1.
    // Products without subscription plans render no subscribe/one-time cards, so they must
    // start in one-time mode — otherwise the buy button would open the subscribe sheet for
    // a plan that does not exist.
    var subsEnabled = data.subsEnabled !== false;
    // Which landing page produced this order. Comes from template.suffix, so it
    // is right whether the visitor was routed or arrived on a direct link, and
    // blank on the ordinary PDP. Read by the router's weight job to score each
    // arm on fuel profit rather than on conversion.
    var lpSuffix = data.lpSuffix || '';
    // Which card opens selected on a product that HAS plans. Subscribe unless the
    // Liquid marks this product one-time-first. Subscribe-by-default reached HJ and
    // PRO in the week of 6 Sep 2026; HJ's add-to-cart rate fell 23.98% -> 10.84% that
    // week and the subscribe flow authorises 15.6% of starts (77 of 495 across both
    // products). HJ opens one-time; PRO is left alone as the control.
    var defaultMode = (data.defaultMode === 'onetime') ? 'onetime' : 'subscribe';
    var state = { weight: null, plan: null, mode: subsEnabled ? defaultMode : 'onetime', extraDogs: [], dog1Name: '', overCap: false };

    // --- DOM refs ---
    var productRoot = document.getElementById('MainProduct-' + sectionId) ||
                      document.querySelector('product-info[data-section="' + sectionId + '"]');

    var planStepEl  = document.getElementById('kv-wps-plan-step-' + sectionId);
    var summaryEl   = document.getElementById('kv-wps-summary-' + sectionId);
    var packsValEl  = document.getElementById('kv-wps-packs-val-' + sectionId);
    var priceValEl  = document.getElementById('kv-wps-price-val-' + sectionId);
    var customAtcEl     = document.getElementById('kv-wps-atc-'          + sectionId);
    var errorEl         = document.getElementById('kv-wps-error-'        + sectionId);
    var compareValEl    = document.getElementById('kv-wps-compare-val-'  + sectionId);
    var savingsRowEl    = document.getElementById('kv-wps-savings-row-'  + sectionId);
    var savingsValEl    = document.getElementById('kv-wps-savings-val-'  + sectionId);
    var dosageInfoEl    = document.getElementById('kv-wps-dosage-info-'   + sectionId);
    var dosageValEl     = document.getElementById('kv-wps-dosage-val-'    + sectionId);
    var dosageToggleEl  = document.getElementById('kv-wps-dosage-toggle-' + sectionId);
    var dosagePanelEl   = document.getElementById('kv-wps-dosage-panel-'  + sectionId);

    // Phase F/4 — subscribe toggle + per-dog configurator refs
    var buyStepEl   = document.getElementById('kv-wps-buy-step-'    + sectionId);
    var subPriceEl  = document.getElementById('kv-wps-sub-price-'   + sectionId);
    var subWasEl    = document.getElementById('kv-wps-sub-was-'     + sectionId);
    var subSaveEl   = document.getElementById('kv-wps-sub-save-'    + sectionId);
    var subRibbonEl = document.getElementById('kv-wps-sub-ribbon-'  + sectionId);
    var subMetaEl   = document.getElementById('kv-wps-sub-meta-'    + sectionId);
    var subCadenceEl= document.getElementById('kv-wps-sub-cadence-' + sectionId);
    var subAssureEl = document.getElementById('kv-wps-sub-assure-'  + sectionId);
    var oncePriceEl = document.getElementById('kv-wps-once-price-'  + sectionId);
    // Phase 4 — per-dog configurator elements
    var weightStepEl    = root.querySelector('.kv-wps__weight-step');
    var doglistHeadEl   = document.getElementById('kv-wps-doglist-head-' + sectionId);
    var dogListEl       = document.getElementById('kv-wps-doglist-'      + sectionId);
    var addDogBtn       = document.getElementById('kv-wps-add-dog-'      + sectionId);
    var splitNoteEl     = document.getElementById('kv-wps-split-note-'   + sectionId);

    // Native buy wrapper (wraps product form + Breeze in the buy_buttons block)
    var nativeBuyWrapper = productRoot
      ? productRoot.querySelector('[data-kv-native-buy="' + sectionId + '"]')
      : null;

    // Hidden variant id input inside the native product form
    var idInput = productRoot
      ? productRoot.querySelector('form[action*="/cart/add"] input[name="id"]')
      : null;

    // --- Hide native Shopify ATC (replaced by our guided button) ---
    if (productRoot) {
      var nativeAtc = productRoot.querySelector('#ProductSubmitButton-' + sectionId);
      if (nativeAtc) nativeAtc.closest('.product-form__buttons').style.display = 'none';
    }

    // --- Weight buttons ---
    root.querySelectorAll('[data-weight-btn]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        state.weight = btn.dataset.weightBtn;
        setActiveBtn(root.querySelectorAll('[data-weight-btn]'), btn);

        // Keep an already-chosen plan across weight changes. Resetting to 30-day here
        // silently downgraded anyone who picked 60 or 90 and then adjusted their dog's
        // weight: 32 of the 68 sessions that chose a long plan and added to cart added
        // the 30-day instead (PostHog, 90d to 26 Sep 2026). applyPlanAvailability still
        // shifts the plan up when the chosen one is out of stock for the new band.
        if (!state.plan) state.plan = '30';

        // Show Step 2
        if (planStepEl) planStepEl.hidden = false;

        // Update ATC prompt to "Choose Your Plan"
        updateAtcPrompt();

        applyPlanAvailability();
      });
    });

    // --- Plan buttons ---
    root.querySelectorAll('[data-plan-btn]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        if (btn.disabled) return; // unavailable plan — ignore click
        state.plan = btn.dataset.planBtn;
        setActiveBtn(root.querySelectorAll('[data-plan-btn]'), btn);
        if (window.posthog) window.posthog.capture('subscribe_plan_selected', { plan_duration: state.plan, buy_mode: state.mode });
        updateUI();
      });
    });

    // --- Dosage toggle ---
    if (dosageToggleEl) {
      dosageToggleEl.addEventListener('click', function () {
        var expanded = dosageToggleEl.getAttribute('aria-expanded') === 'true';
        dosageToggleEl.setAttribute('aria-expanded', expanded ? 'false' : 'true');
        if (dosagePanelEl) dosagePanelEl.classList.toggle('kv-wps__dosage-panel--open', !expanded);
      });
    }

    // --- Subscribe / One-time toggle (Phase F) ---
    root.querySelectorAll('[data-buy-mode]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        state.mode = btn.dataset.buyMode;
        // Default-leak signal: how many people switch OFF subscribe (and at which plan).
        if (window.posthog) window.posthog.capture('subscribe_toggle_changed', { to_mode: state.mode, plan_duration: state.plan });
        root.querySelectorAll('[data-buy-mode]').forEach(function (b) {
          var on = b === btn;
          b.classList.toggle('kv-wps__opt--active', on);
          b.setAttribute('aria-pressed', on ? 'true' : 'false');
        });
        updateUI();
      });
    });

    // --- Per-dog configurator (Phase 4) ---
    if (addDogBtn) addDogBtn.addEventListener('click', addExtraDog);
    renderDogList(); // set initial visibility (single-dog: Step 1 shown, list empty)

    function totalDogs() { return 1 + state.extraDogs.length; }

    // Every dog with its OWN dose. state.weight is Dog 1's band only, so a household
    // with mixed weights must never render one band's scoop count for all of them
    // (a 12-26 kg dog takes 1 scoop, a 27-45 kg dog takes 2 — printing "2 scoops" for
    // both overfeeds the small one).
    function dogDoseList() {
      var rows = [{ label: (state.dog1Name || '').trim() || 'Dog 1', band: state.weight }];
      state.extraDogs.forEach(function (e, i) {
        rows.push({ label: (e.name || '').trim() || ('Dog ' + (i + 2)), band: e.weight_band });
      });
      return rows.map(function (r) {
        var d = dosageConfig[r.band];
        return { label: r.label, text: d ? d.text : '', grams: d ? d.grams : '' };
      });
    }

    function addExtraDog() {
      if (state.extraDogs.length >= MAX_DOGS - 1) return;
      // New dogs default their band to Dog 1's current band.
      state.extraDogs.push({ name: '', weight_band: state.weight || '27-45' });
      renderDogList();
      updateUI();
    }

    function removeExtraDog(i) {
      state.extraDogs.splice(i, 1);
      // Back to a single dog: restore Step 1 and re-sync its active weight button.
      if (state.extraDogs.length === 0) {
        var wb = root.querySelector('[data-weight-btn="' + state.weight + '"]');
        if (wb) setActiveBtn(root.querySelectorAll('[data-weight-btn]'), wb);
      }
      renderDogList();
      updateUI();
    }

    // Build one uniform dog row. The Dog 1 row drives state.weight (+ Step 1 side
    // effects) and has no remove button; extra rows drive state.extraDogs[idx].
    function buildDogRow(opts) {
      var row = document.createElement('div');
      row.className = 'kv-wps__dogrow';

      var nameInput = document.createElement('input');
      nameInput.type = 'text';
      nameInput.className = 'kv-wps__dogrow-name';
      nameInput.placeholder = 'Name (optional)';
      nameInput.maxLength = 30;
      nameInput.autocomplete = 'off';
      nameInput.value = opts.name || '';
      nameInput.setAttribute('aria-label', opts.label + ' name (optional)');
      nameInput.addEventListener('input', function () {
        if (opts.isDog1) state.dog1Name = nameInput.value;
        else state.extraDogs[opts.idx].name = nameInput.value;
      });

      var sel = document.createElement('select');
      sel.className = 'kv-wps__dogrow-weight';
      sel.setAttribute('aria-label', opts.label + ' weight');
      ['4-11', '12-26', '27-45', '46+'].forEach(function (band) {
        var opt = document.createElement('option');
        opt.value = band;
        opt.textContent = WEIGHT_LABELS[band];
        if (band === opts.band) opt.selected = true;
        sel.appendChild(opt);
      });
      sel.addEventListener('change', function () {
        if (opts.isDog1) { state.weight = sel.value; applyPlanAvailability(); }
        else { state.extraDogs[opts.idx].weight_band = sel.value; updateUI(); }
      });

      var price = document.createElement('span');
      price.className = 'kv-wps__dogrow-price';
      price.setAttribute('data-dog-price', opts.isDog1 ? '__dog1' : String(opts.idx));

      row.appendChild(nameInput);
      row.appendChild(sel);
      row.appendChild(price);

      if (opts.isDog1) {
        // Inert spacer keeps the grid aligned with extra rows' remove button.
        var spacer = document.createElement('span');
        spacer.className = 'kv-wps__dogrow-remove kv-wps__dogrow-remove--hidden';
        spacer.setAttribute('aria-hidden', 'true');
        row.appendChild(spacer);
      } else {
        var remove = document.createElement('button');
        remove.type = 'button';
        remove.className = 'kv-wps__dogrow-remove';
        remove.setAttribute('aria-label', 'Remove ' + opts.label);
        remove.innerHTML = '&times;';
        remove.addEventListener('click', function () { removeExtraDog(opts.idx); });
        row.appendChild(remove);
      }
      return row;
    }

    // Rebuild the whole dog list. Rebuilt on add/remove only (not on price refresh)
    // so half-typed names survive. When multi-dog, Step 1's weight buttons hide and
    // EVERY dog (incl. Dog 1) becomes a uniform row.
    function renderDogList() {
      var multi = state.extraDogs.length > 0;
      if (weightStepEl)  weightStepEl.hidden  = multi;
      if (doglistHeadEl) doglistHeadEl.hidden = !multi;
      if (addDogBtn) {
        var atMax = state.extraDogs.length >= MAX_DOGS - 1;
        addDogBtn.disabled = atMax;
        addDogBtn.classList.toggle('kv-wps__add-dog--disabled', atMax);
      }
      if (!dogListEl) return;
      dogListEl.innerHTML = '';
      if (!multi) return; // single dog: Step 1 is the input, no rows
      dogListEl.appendChild(buildDogRow({ isDog1: true, name: state.dog1Name, band: state.weight, label: 'Dog 1' }));
      state.extraDogs.forEach(function (dog, i) {
        dogListEl.appendChild(buildDogRow({ isDog1: false, idx: i, name: dog.name, band: dog.weight_band, label: 'Dog ' + (i + 2) }));
      });
    }

    // --- Custom ATC button — mode-aware (subscribe → QuickBuy sheet, one-time → cart) ---
    if (customAtcEl) {
      customAtcEl.addEventListener('click', function () {
        if (!state.weight || !state.plan) return;
        var config = getConfig();
        if (!config) return;
        if (state.mode === 'subscribe' && subsEnabled) startSubscribe();
        else addToCartOneTime();
      });
    }

    function startSubscribe() {
      // Second line of defence: never open the sheet for a household over the UPI
      // AutoPay cap, even if the button state was bypassed.
      if (state.overCap) return;
      if (window.KVQuickBuy && typeof window.KVQuickBuy.openSubscribe === 'function') {
        if (state.extraDogs.length === 0) {
          // Single dog — v1 payload, identical to the pre-Phase-4 call.
          window.KVQuickBuy.openSubscribe({
            product: productLabel,
            weight_band: state.weight,
            duration: parseInt(state.plan, 10),
            quantity: 1,
            dog_id: window.kvDogId || null
          });
        } else {
          // Multiple dogs — v2 payload: a per-dog array (Dog 1 + each extra dog).
          window.KVQuickBuy.openSubscribe({
            product: productLabel,
            duration: parseInt(state.plan, 10),
            dogs: [{ name: state.dog1Name || 'Dog 1', weight_band: state.weight }].concat(
              state.extraDogs.map(function (e, i) {
                return { name: e.name || ('Dog ' + (i + 2)), weight_band: e.weight_band };
              })
            )
          });
        }
      } else {
        showError('One moment — subscriptions are still loading. Please try again.');
      }
    }

    // --- Inventory helpers ---

    function isPlanAvailable(weight, plan) {
      var cfg = (planConfig[weight] || {})[plan];
      if (!cfg) return false;
      return cfg.items.every(function (item) {
        var vid = resolveVariantId(item.title);
        if (vid == null) return true; // unknown variant → optimistic
        return inventoryMap[String(vid)] !== false;
      });
    }

    function applyPlanAvailability() {
      var PLAN_ORDER = ['30', '60', '90'];
      var planBtns = root.querySelectorAll('[data-plan-btn]');

      // Mark each plan button available / unavailable
      planBtns.forEach(function (btn) {
        var avail = isPlanAvailable(state.weight, btn.dataset.planBtn);
        btn.disabled = !avail;
        btn.classList.toggle('kv-wps__btn--unavailable', !avail);
        btn.setAttribute('aria-disabled', avail ? 'false' : 'true');
      });

      // Find the first available plan starting from desired (default: 30)
      var desired  = state.plan || '30';
      var startIdx = PLAN_ORDER.indexOf(desired);
      if (startIdx === -1) startIdx = 0;
      var resolved = null;
      var shifted  = false;

      for (var i = startIdx; i < PLAN_ORDER.length; i++) {
        if (isPlanAvailable(state.weight, PLAN_ORDER[i])) {
          resolved = PLAN_ORDER[i];
          if (i !== startIdx) shifted = true;
          break;
        }
      }

      if (resolved) {
        state.plan = resolved;
        planBtns.forEach(function (b) {
          var isActive = b.dataset.planBtn === resolved;
          b.classList.toggle('kv-wps__btn--active', isActive);
          b.setAttribute('aria-pressed', isActive ? 'true' : 'false');
        });
        updateUI();
      } else {
        // All plans unavailable
        state.plan = null;
        planBtns.forEach(function (b) {
          b.classList.remove('kv-wps__btn--active');
          b.setAttribute('aria-pressed', 'false');
        });
        showAllUnavailable();
      }
    }

    function showAllUnavailable() {
      if (summaryEl)   summaryEl.hidden   = true;
      if (customAtcEl) {
        customAtcEl.hidden   = false;
        customAtcEl.disabled = true;
        var textEl = customAtcEl.querySelector('.kv-wps__atc-text');
        if (textEl) textEl.textContent = 'Currently Unavailable';
      }
    }

    // --- ATC prompt state management ---
    function updateAtcPrompt() {
      if (!customAtcEl) return;
      var textEl = customAtcEl.querySelector('.kv-wps__atc-text');
      if (!state.weight) {
        customAtcEl.disabled = true;
        customAtcEl.classList.add('kv-wps__custom-atc--prompt');
        customAtcEl.classList.remove('button--primary');
        if (textEl) textEl.textContent = 'Select Weight Above';
      } else if (!state.plan) {
        customAtcEl.disabled = true;
        customAtcEl.classList.add('kv-wps__custom-atc--prompt');
        customAtcEl.classList.remove('button--primary');
        if (textEl) textEl.textContent = 'Select Your Plan';
      }
    }

    // --- Update UI after each selection change ---
    function updateUI() {
      hideError();
      // Reset ATC to active state
      if (customAtcEl) {
        customAtcEl.disabled = false;
        customAtcEl.classList.remove('kv-wps__custom-atc--prompt');
        customAtcEl.classList.add('button--primary');
        var atcText = customAtcEl.querySelector('.kv-wps__atc-text');
        if (atcText) atcText.textContent = state.mode === 'subscribe' ? 'Start my plan' : 'Add to Cart';
      }

      if (!state.weight || !state.plan) {
        if (summaryEl) summaryEl.hidden = true;
        if (buyStepEl) buyStepEl.hidden = true;
        updateAtcPrompt();
        return;
      }

      var config = getConfig();
      if (!config) return;

      // Build summary
      var packParts      = [];
      var totalPrice     = 0;
      var totalCompareAt = 0;

      config.items.forEach(function (item) {
        var vid = resolveVariantId(item.title);
        if (vid != null) {
          totalPrice     += (priceMap[String(vid)]          || 0) * item.qty;
          totalCompareAt += (compareAtPriceMap[String(vid)] || 0) * item.qty;
        }
        packParts.push(item.qty > 1 ? item.qty + '\u00d7 ' + item.title : '1\u00d7 ' + item.title);
      });

      if (packsValEl) {
        var includesText = 'Includes: ' + packParts.join(' + ');
        if (jerkyOnPlan() && jerkyAvailable) includesText += ' + Free Chicken Jerky (100g) worth ₹599';
        packsValEl.textContent = includesText;
      }
      if (priceValEl) priceValEl.textContent  = formatMoney(totalPrice);
      if (summaryEl)  summaryEl.hidden        = false;

      // Compare-at price + savings
      // Hide savings badge for 4-11 kg / 30-day (120g single pack — no multi-pack discount)
      var savings = totalCompareAt - totalPrice;
      var hasSavings = totalCompareAt > 0 && savings >= 100;
      if (state.weight === '4-11' && state.plan === '30') { hasSavings = false; }
      if (compareValEl) {
        compareValEl.textContent = hasSavings ? formatMoney(totalCompareAt) : '';
        compareValEl.hidden      = !hasSavings;
      }
      if (savingsRowEl) {
        savingsRowEl.hidden = !hasSavings;
        if (savingsValEl) {
          savingsValEl.textContent = hasSavings ? '\u00a0' + formatMoney(savings) : '';
        }
      }

      // Dosage info — one line per dog, since each dog's band sets its own scoop count.
      if (dosageValEl) {
        var doses = dogDoseList();
        if (doses.length === 1) {
          dosageValEl.textContent = doses[0].text ? doses[0].text + ' (' + doses[0].grams + ')' : '';
        } else {
          dosageValEl.textContent = doses.filter(function (x) { return x.text; }).map(function (x) {
            return x.label + ': ' + x.text.replace(' per day', '') + ' (' + x.grams + ')';
          }).join(' · ');
        }
      }
      if (dosageInfoEl) dosageInfoEl.hidden = false;

      // Phase F — populate subscribe/one-time cards + reveal the dogs + buy steps
      updateBuyCards();

      // All plans use custom AJAX ATC — native buy form is permanently hidden
      if (customAtcEl) customAtcEl.hidden = false;

      // Sync gallery image to active weight + plan
      var gallery = productRoot
        ? productRoot.closest('.shopify-section').querySelector('media-gallery')
        : document.querySelector('media-gallery');

      // 1. Try combo image mapping (position-based, immune to AVADA alt rewrites)
      var comboKey = state.weight + ':' + state.plan;
      var planImages = (window.kvPlanImages || {})[sectionId] || {};
      var comboMediaId = planImages[comboKey];

      if (comboMediaId && gallery && gallery.setActiveMedia) {
        gallery.setActiveMedia(comboMediaId, true, true);
      } else if (idInput) {
        // 2. Fallback: switch via variant's featured media (Liquid-built mapping)
        var primaryItem = config.items[0];
        var primaryVariantId = resolveVariantId(primaryItem.title);
        if (primaryVariantId) {
          idInput.value = primaryVariantId;
          var variantMedia = (window.kvVariantMedia || {})[sectionId] || {};
          var galleryMediaId = variantMedia[String(primaryVariantId)];
          if (galleryMediaId && gallery && gallery.setActiveMedia) {
            gallery.setActiveMedia(galleryMediaId, true, true);
          } else {
            // Last resort: trigger change event for Dawn's handler
            idInput.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }
      }
    }

    // --- Helpers ---

    function getConfig() {
      if (!state.weight || !state.plan) return null;
      return planConfig[state.weight] && planConfig[state.weight][state.plan];
    }

    /**
     * Resolve a size label (e.g. "120g") to a real Shopify variant ID.
     * Tries multiple strategies to handle varied variant title formats.
     */
    function resolveVariantId(sizeLabel) {
      if (!sizeLabel) return null;
      var lc = sizeLabel.toLowerCase().trim();

      // 1. Exact match in variantMap
      if (variantMap[sizeLabel] !== undefined) return variantMap[sizeLabel];

      // 2. Case-insensitive exact match in variantMap keys
      for (var key in variantMap) {
        if (key.toLowerCase().trim() === lc) return variantMap[key];
      }

      // 3. Match against actual variant objects via window.themeProductVariants
      var allVariants = window.themeProductVariants && window.themeProductVariants[sectionId];
      if (allVariants) {
        var i, v, j;

        // 3a. option1 exact (case-insensitive)
        for (i = 0; i < allVariants.length; i++) {
          v = allVariants[i];
          if (v.option1 && v.option1.toLowerCase().trim() === lc) return v.id;
        }

        // 3b. Any option value (case-insensitive)
        for (i = 0; i < allVariants.length; i++) {
          v = allVariants[i];
          if (v.options) {
            for (j = 0; j < v.options.length; j++) {
              if (String(v.options[j]).toLowerCase().trim() === lc) return v.id;
            }
          }
        }

        // 3c. Numeric-only match — "120g" matches "120 g", "120gm", "120 Grams", etc.
        var numLabel = lc.replace(/[^0-9]/g, '');
        if (numLabel) {
          for (i = 0; i < allVariants.length; i++) {
            v = allVariants[i];
            // Check option1 numerically
            var numOpt1 = String(v.option1 || '').toLowerCase().replace(/[^0-9]/g, '');
            if (numOpt1 === numLabel) return v.id;
            // Check all options numerically
            if (v.options) {
              for (j = 0; j < v.options.length; j++) {
                var numOptJ = String(v.options[j]).toLowerCase().replace(/[^0-9]/g, '');
                if (numOptJ === numLabel) return v.id;
              }
            }
          }

          // 3d. Numeric match against variantMap keys
          for (var mapKey in variantMap) {
            var numMapKey = mapKey.toLowerCase().replace(/[^0-9]/g, '');
            if (numMapKey === numLabel) return variantMap[mapKey];
          }
        }
      }

      return null;
    }

    function formatMoney(cents) {
      var amount = Math.round(cents / 100);
      return '\u20b9' + amount.toLocaleString('en-IN');
    }

    // Phase F \u2014 format paise; dec=true shows 2 decimals (subscribe prices carry paise)
    function formatPaise(paise, dec) {
      var rupees = paise / 100;
      return '\u20b9' + rupees.toLocaleString('en-IN', dec
        ? { minimumFractionDigits: 2, maximumFractionDigits: 2 }
        : { maximumFractionDigits: 0 });
    }

    // Phase 4 \u2014 per-band pricing (paise). Reused for Dog 1 + every extra dog.
    function perDogOncePaise(band) {
      var cfg = planConfig[band] && planConfig[band][state.plan];
      if (!cfg) return 0;
      var total = 0;
      cfg.items.forEach(function (item) {
        var vid = resolveVariantId(item.title);
        if (vid != null) total += (priceMap[String(vid)] || 0) * item.qty;
      });
      return total;
    }

    function perDogSubPaise(band) {
      var factor = state.plan === '90' ? 0.80 : 0.85;
      return Math.round(perDogOncePaise(band) * factor);
    }

    // Same maths as above but for a cycle we are NOT currently on, so we can answer
    // "would these dogs fit under the UPI cap on a shorter cycle?" without touching state.
    function perDogSubPaiseForPlan(band, plan) {
      var cfg = planConfig[band] && planConfig[band][plan];
      if (!cfg) return 0;
      var once = 0;
      cfg.items.forEach(function (item) {
        var vid = resolveVariantId(item.title);
        if (vid != null) once += (priceMap[String(vid)] || 0) * item.qty;
      });
      return Math.round(once * (plan === '90' ? 0.80 : 0.85));
    }

    function subTotalForPlan(plan) {
      var t = perDogSubPaiseForPlan(state.weight, plan);
      state.extraDogs.forEach(function (e) { t += perDogSubPaiseForPlan(e.weight_band, plan); });
      return t;
    }

    // The UPI cap is per CHARGE, not per household, so the same dogs often fit on a
    // shorter cycle. Prefer the LONGEST cycle that fits, because longer cycles discount
    // more. Returns null when even the 30 day cycle is over the cap (5+ giant dogs),
    // which is when the only honest advice is to split the household.
    function bestShorterPlan() {
      var current = parseInt(state.plan, 10);
      var found = null;
      ['90', '60', '30'].forEach(function (p) {
        if (found || parseInt(p, 10) >= current) return;
        var t = subTotalForPlan(p);
        if (t <= MANDATE_CAP_PAISE) found = { plan: p, total: t };
      });
      return found;
    }

    // Phase 4 \u2014 combined subscribe/one-time totals across Dog 1 + every extra dog,
    // populate the buy cards, per-dog row prices, split note, reveal the buy step.
    function updateBuyCards() {
      if (!state.weight || !state.plan) return;
      var config = getConfig();
      if (!config) return;

      // Combined totals: Dog 1 (state.weight) + each extra dog's own band.
      var onceTot = perDogOncePaise(state.weight);
      var subTot  = perDogSubPaise(state.weight);
      state.extraDogs.forEach(function (e) {
        onceTot += perDogOncePaise(e.weight_band);
        subTot  += perDogSubPaise(e.weight_band);
      });
      var saveTot = onceTot - subTot;

      if (subPriceEl)   subPriceEl.textContent   = formatPaise(subTot, true);
      if (subWasEl)     subWasEl.textContent      = formatPaise(onceTot, false);
      if (subSaveEl) {
        // A saving exists only while actually subscribing. In one-time mode — and on the
        // products that carry no subscription at all — there is nothing being saved.
        var claimSave = state.mode === 'subscribe' && saveTot > 0;
        subSaveEl.textContent = claimSave ? 'You save ' + formatPaise(saveTot, true) : '';
        subSaveEl.hidden = !claimSave;
      }
      if (subRibbonEl)  subRibbonEl.textContent   = (state.plan === '90' ? '20%' : '15%') + ' off';
      if (subCadenceEl) subCadenceEl.textContent  = 'Delivery every ' + state.plan + ' days';
      if (subAssureEl)  subAssureEl.hidden         = state.mode !== 'subscribe';
      if (oncePriceEl)  oncePriceEl.textContent   = formatPaise(onceTot, false);

      // Per-dog row prices (each dog's OWN band, mode-aware) \u2014 Dog 1 + extras.
      if (dogListEl) {
        var d1El = dogListEl.querySelector('[data-dog-price="__dog1"]');
        if (d1El) d1El.textContent = formatPaise(state.mode === 'subscribe' ? perDogSubPaise(state.weight) : perDogOncePaise(state.weight), false);
        state.extraDogs.forEach(function (e, i) {
          var el = dogListEl.querySelector('[data-dog-price="' + i + '"]');
          if (!el) return;
          var p = state.mode === 'subscribe' ? perDogSubPaise(e.weight_band) : perDogOncePaise(e.weight_band);
          el.textContent = formatPaise(p, false);
        });
      }

      // Split note: subscribe-only (it is a UPI-AutoPay mandate cap, not a one-time
      // limit) and only when the combined subscribe total tops \u20b915,000.
      state.overCap = state.mode === 'subscribe' && subTot > MANDATE_CAP_PAISE;
      state.capFix = state.overCap ? bestShorterPlan() : null;
      if (splitNoteEl) {
        if (state.overCap) {
          var capLead = 'UPI AutoPay allows up to \u20b915,000 per delivery. This plan comes to ' +
            formatPaise(subTot, true) + '. ';
          if (state.capFix) {
            // Keep the whole household on one plan by shortening the cycle. Smaller
            // charge, same dogs, same single mandate.
            // Every value below is generated here (static copy, formatted rupees, a plan
            // literal, a dog count). No customer input reaches this string.
            splitNoteEl.innerHTML = capLead +
              'Switch to the ' + state.capFix.plan + ' day plan and all ' + totalDogs() +
              ' dogs stay on one plan at ' + formatPaise(state.capFix.total, true) +
              ' per delivery.' +
              '<button type="button" class="kv-wps__cap-switch" data-cap-switch="' + state.capFix.plan + '">' +
              'Switch to the ' + state.capFix.plan + ' day plan</button>';
            var switchBtn = splitNoteEl.querySelector('[data-cap-switch]');
            if (switchBtn) {
              switchBtn.addEventListener('click', function () {
                var target = document.querySelector('[data-plan-btn="' + this.getAttribute('data-cap-switch') + '"]');
                if (target) target.click();
              });
            }
          } else {
            // Even the shortest cycle is over the cap, so splitting is the only option.
            splitNoteEl.textContent = capLead +
              'Start a plan for fewer dogs and add another plan for the rest, or choose One time above.';
          }
          splitNoteEl.hidden = false;
        } else {
          splitNoteEl.hidden = true;
        }
      }
      // Block the CTA itself. updateUI() re-enables the button before calling this,
      // so disabling here is what actually sticks.
      if (customAtcEl && state.overCap) {
        customAtcEl.disabled = true;
        customAtcEl.classList.add('kv-wps__custom-atc--prompt');
        customAtcEl.classList.remove('button--primary');
        var capTextEl = customAtcEl.querySelector('.kv-wps__atc-text');
        // Match the CTA to the advice above it: a shorter cycle keeps every dog, so
        // telling them to remove one would be wrong whenever a cycle actually fits.
        if (capTextEl) {
          capTextEl.textContent = state.capFix
            ? 'Pick a shorter plan to continue'
            : 'Remove a dog to continue';
        }
      }

      // Subscribe meta subline (single-dog keeps the original phrasing).
      var dose = dosageConfig[state.weight];
      var doseTxt = dose ? dose.text.replace(' per day', '/day') : '';
      if (subMetaEl) {
        if (state.extraDogs.length === 0) {
          var packs = config.items.map(function (item) { return item.qty + '\u00d7 ' + item.title; });
          // 'every 30 days' describes a repeating delivery. Say that only when one is
          // actually being set up; otherwise describe how long the pack lasts.
          var recurring = state.mode === 'subscribe';
          var meta = packs.join(' + ') +
            (recurring ? ' every ' + state.plan + ' days' : ' \u00b7 ' + state.plan + ' day supply');
          if (doseTxt) meta += ' \u00b7 ' + doseTxt;
          subMetaEl.textContent = meta;
        } else {
          subMetaEl.textContent = totalDogs() + ' dogs \u00b7 ' + (state.mode === 'subscribe'
            ? 'one delivery every ' + state.plan + ' days'
            : state.plan + ' day supply');
        }
      }

      // Includes line (only rendered if the element exists in this template).
      if (packsValEl) {
        if (state.extraDogs.length === 0) {
          var inc = 'Includes: ' + config.items.map(function (item) { return item.qty + '\u00d7 ' + item.title; }).join(' + ');
          if (jerkyOnPlan() && jerkyAvailable) inc += ' + Free Chicken Jerky (100g)';
          packsValEl.textContent = inc;
        } else {
          packsValEl.textContent = 'Includes packs for all ' + totalDogs() + ' dogs';
        }
      }

      if (buyStepEl) buyStepEl.hidden = false;
    }

    function setActiveBtn(btnList, activeBtn) {
      btnList.forEach(function (b) {
        var isActive = b === activeBtn;
        b.classList.toggle('kv-wps__btn--active', isActive);
        b.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      });
    }

    function showNativeBuy() {
      if (nativeBuyWrapper) nativeBuyWrapper.style.display = '';
    }

    function hideNativeBuy() {
      if (nativeBuyWrapper) nativeBuyWrapper.style.display = 'none';
    }

    function hideError() {
      if (errorEl) { errorEl.textContent = ''; errorEl.hidden = true; }
    }

    function showError(msg) {
      if (!errorEl) return;
      errorEl.textContent = msg;
      errorEl.hidden = false;
    }

    // --- Add to cart (one-time, 60/90 day) ---
    // Phase 4: builds the cart from EVERY dog's SKUs (Dog 1 at state.weight + each
    // extra dog at its own band), merging duplicate variant ids by summing quantity.
    // Single-dog (no extras) yields the exact same items as the pre-Phase-4 path.
    async function addToCartOneTime() {
      if (!customAtcEl) return;

      var textEl    = customAtcEl.querySelector('.kv-wps__atc-text');
      var loadingEl = customAtcEl.querySelector('.kv-wps__atc-loading');

      // Build the merged cart item list across all dogs (Dog 1 first, then extras).
      var bands = [state.weight].concat(state.extraDogs.map(function (e) { return e.weight_band; }));
      var cartItems = [];
      var idIndex = {};
      var missingTitle = null;
      for (var d = 0; d < bands.length && !missingTitle; d++) {
        var cfg = planConfig[bands[d]] && planConfig[bands[d]][state.plan];
        if (!cfg) continue;
        for (var k = 0; k < cfg.items.length; k++) {
          var it = cfg.items[k];
          var vid = resolveVariantId(it.title);
          if (vid == null) { missingTitle = it.title; break; }
          var key = String(vid);
          if (idIndex[key] != null) {
            cartItems[idIndex[key]].quantity += it.qty;
          } else {
            idIndex[key] = cartItems.length;
            cartItems.push({ id: vid, quantity: it.qty });
          }
        }
      }

      // PostHog: track ATC click
      if (window.posthog) {
        window.posthog.capture('atc_click', {
          product: document.querySelector('h1') ? document.querySelector('h1').textContent.trim() : '',
          items: cartItems.length,
          dogs: totalDogs(),
          weight_band: state.weight,
          plan_duration: state.plan,
          rebuy_ready: !!(window.Rebuy && window.Rebuy.SmartCart && typeof window.Rebuy.SmartCart.show === 'function')
        });
      }

      // Loading state
      customAtcEl.disabled = true;
      if (textEl)    textEl.classList.add('hidden');
      if (loadingEl) loadingEl.classList.remove('hidden');
      hideError();

      if (missingTitle) {
        showError('Unable to find "' + missingTitle + '" variant. Please refresh the page.');
        resetAtcBtn(textEl, loadingEl);
        return;
      }

      // Save dog weight band + plan duration as visible line item properties.
      // Order email, Shopify Admin, webhook payloads, Klaviyo events all see
      // these — required for post-purchase WhatsApp (delivered celebration,
      // first-dose scoop chart) and Klaviyo profile sync.
      var weightLabelMap = {
        '4-11':  'Upto 11 kg',
        '12-26': '12-26 kg',
        '27-45': '27-45 kg',
        '46+':   'Over 46 kg'
      };
      var planLabelMap = {
        '30': '30 Day Plan',
        '60': '60 Day Plan',
        '90': '90 Day Plan'
      };
      var lineProps = {
        'Dog weight': weightLabelMap[state.weight] || state.weight,
        'Plan':       planLabelMap[state.plan]     || state.plan
      };
      // Underscore prefix keeps it out of the customer's view of the line item.
      if (lpSuffix) { lineProps['_kv_lp'] = lpSuffix; }
      cartItems.forEach(function (ci) { ci.properties = lineProps; });

      /* ACQ Free Jerky hook (S144 2026-05-14): if the LP gift banner snippet
         is present AND its checkbox is ticked, append the jerky variant to
         the SAME bulk /cart/add.js POST. Two parallel POSTs to /cart/add.js
         race in Shopify and one item gets dropped (S144 scar v5.0: parent
         product lost, only jerky in cart). Single atomic bulk add is the only
         safe pattern.

         Sequencing (v5.2): capture the BXGY discount in Shopify's session
         BEFORE the bulk add. Then Shopify auto-applies the BXGY during the
         add, the /cart/add.js response already includes cart.discount_codes,
         and Rebuy's cart cache (set when it sees the cart:add event) is
         correct from the start. Without this pre-capture, Rebuy caches the
         pre-discount cart and `show()` renders it stale until a page refresh.
         (v5.1 attempted post-add discount apply + delayed open — discount
         landed in cart object but Rebuy's cache was already set from the
         add response, before the discount was applied.) */
      var giftCheckbox = document.getElementById('kv-gift-checkbox');
      var giftEnabled  = !!(giftCheckbox && giftCheckbox.checked);
      // Free jerky: auto-included on the 90-day plan (any band/supplement) when in
      // stock, OR via the ACQ gift checkbox. The 90-day path uses the dedicated
      // K990FREEJERKY BXGY (no once_per_customer, so every 90-day buyer gets it).
      var planJerky = jerkyOnPlan() && jerkyAvailable && !!jerkyVariantId;
      var addJerky  = (giftEnabled || planJerky) && !!jerkyVariantId;
      var jerkyCode = planJerky ? jerkyDiscountCode : 'FREECHICKENJERKY100GMS';
      // One free jerky per order. Both jerky codes allow one use per order, so a
      // second plan added to the same cart must not add a second pack: checkout
      // would charge it, and the guard below would then remove the whole jerky
      // line, including the pack that was free. The code is still captured.
      var jerkyWanted = addJerky;
      if (addJerky) {
        try {
          var cartNow = await fetch('/cart.js', { credentials: 'same-origin' }).then(function (r) { return r.json(); });
          if ((cartNow.items || []).some(function (it) { return it.variant_id === jerkyVariantId; })) addJerky = false;
        } catch (cartErr) {}
      }
      if (addJerky) {
        cartItems.push({
          id:         jerkyVariantId,
          quantity:   1,
          properties: planJerky ? { '_kv_free_jerky': state.plan + 'day' } : { '_kv_acq_gift': 'true' }
        });
      }

      try {
        // Pre-capture the BXGY discount BEFORE the bulk add. /discount/CODE
        // writes the code into Shopify's session cookie; the subsequent
        // /cart/add.js then auto-applies the BXGY during the add itself
        // (response cart.discount_codes is populated on return).
        // A second code only survives alongside the jerky BXGY if it is a different
        // discount CLASS — the BXGY is PRODUCT, so extraCode must be ORDER level.
        // ONE CODE PER REQUEST: /discount/A,B stores the literal string "A,B" as a
        // single code, matches nothing, and NEITHER discount applies, so the "free"
        // jerky gets charged. Verified 2026-09-12: comma-joined gave Rs 8,096,
        // sequential gave Rs 6,747.30 with both codes applicable.
        var preCodes = [];
        if (jerkyWanted) preCodes.push(jerkyCode);
        var extraCode  = offer().extraCode;
        var extraPlans = (offer().extraPlans || []).map(String);
        if (extraCode && extraPlans.indexOf(String(state.plan)) !== -1) preCodes.push(extraCode);
        for (var ci = 0; ci < preCodes.length; ci++) {
          try {
            await fetch('/discount/' + encodeURIComponent(preCodes[ci]) + '?redirect=/cart.js', {
              method:      'GET',
              credentials: 'same-origin'
            });
          } catch (preErr) {
            console.warn('[kv-wps] pre-discount capture failed:', preCodes[ci], preErr);
            // Continue anyway — bulk add still works, discount may not apply
          }
        }

        var res = await fetch('/cart/add.js', {
          method:  'POST',
          headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body:    JSON.stringify({ items: cartItems })
        });

        if (!res.ok) {
          var errData = {};
          try { errData = await res.json(); } catch (e) {}
          showError(errData.description || 'Failed to add to cart. Please try again.');
          resetAtcBtn(textEl, loadingEl);
          return;
        }

        // GA4 + Meta Pixel: handled by Shopify's native App Pixel (Google & YouTube app + Meta Pixel) and Conversions API
        // No manual gtag/fbq call — avoids double-firing with server-side events

        // Defensive: confirm cart actually has the discount applied. If not
        // (rare — session race or BXGY conditions not met), fall back to the
        // global applyDiscountToCart poll before opening the drawer. This
        // protects against Rebuy showing a stale full-price cart.
        if (addJerky) {
          try {
            var cartCheck = await fetch('/cart.js', { credentials: 'same-origin' }).then(function (r) { return r.json(); });
            var hasDiscount = (cartCheck.discount_codes || []).some(function (d) {
              return d.code && d.code.toUpperCase() === jerkyCode.toUpperCase() && d.applicable !== false;
            });
            if (!hasDiscount && typeof window.applyDiscountToCart === 'function') {
              console.log('[kv-wps] BXGY not auto-applied, forcing apply...');
              await new Promise(function (resolve) {
                window.applyDiscountToCart(jerkyCode, function () {
                  document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
                  resolve();
                });
              });
              cartCheck = await fetch('/cart.js', { credentials: 'same-origin' }).then(function (r) { return r.json(); });
            }
            // SAFETY GUARD: never charge a surprise full-price jerky. If the jerky
            // line is still in the cart at a non-zero price (discount failed to
            // apply), remove it — the customer keeps their plan, we just drop the
            // free gift rather than bill Rs.599 for it.
            var jerkyLine = (cartCheck.items || []).filter(function (it) {
              return it.variant_id === jerkyVariantId;
            })[0];
            if (jerkyLine && jerkyLine.final_line_price > 0) {
              await fetch('/cart/change.js', {
                method:  'POST',
                headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
                body:    JSON.stringify({ id: jerkyLine.key, quantity: 0 })
              });
              console.warn('[kv-wps] free jerky not discounted — removed to avoid Rs.599 charge');
            }
          } catch (checkErr) {
            console.warn('[kv-wps] cart discount check failed:', checkErr);
          }
        }

        // Success path — open cart (handles Rebuy race condition)
        var rebuyReady = window.Rebuy && window.Rebuy.SmartCart &&
            typeof window.Rebuy.SmartCart.show === 'function';

        triggerCartOpen();

        if (rebuyReady) {
          if (textEl) { textEl.textContent = 'Added!'; textEl.classList.remove('hidden'); }
          if (loadingEl) loadingEl.classList.add('hidden');
          customAtcEl.disabled = false;
          setTimeout(function () {
            if (textEl) textEl.textContent = 'Add to Cart';
          }, 2000);
        }
        // If Rebuy wasn't ready, triggerCartOpen() handles its own UI state

      } catch (e) {
        showError('Something went wrong. Please try again.');
        resetAtcBtn(textEl, loadingEl);
      }
    }

    function resetAtcBtn(textEl, loadingEl) {
      if (customAtcEl) customAtcEl.disabled = false;
      if (textEl)    { textEl.textContent = 'Add to Cart'; textEl.classList.remove('hidden'); }
      if (loadingEl) loadingEl.classList.add('hidden');
    }

    // --- Trigger Rebuy / cart open (with race condition fix) ---
    // Priority: 1) Rebuy Smart Cart  2) Dawn cart drawer  3) /checkout
    function triggerCartOpen() {
      // If Rebuy is ready, open immediately
      if (window.Rebuy && window.Rebuy.SmartCart &&
          typeof window.Rebuy.SmartCart.show === 'function') {
        window.Rebuy.SmartCart.show();
        if (window.posthog) window.posthog.capture('cart_drawer_open', { source: 'rebuy', wait_ms: 0 });
        return;
      }

      // Rebuy not ready — show loading spinner and poll until it loads
      if (customAtcEl) {
        customAtcEl.disabled = true;
        var btnText = customAtcEl.querySelector('.kv-wps__atc-text');
        var btnLoading = customAtcEl.querySelector('.kv-wps__atc-loading');
        if (btnText) btnText.classList.add('hidden');
        if (btnLoading) btnLoading.classList.remove('hidden');
      }

      var maxWait = 5000;
      var waited = 0;
      var pollInterval = 500;

      var poll = setInterval(function () {
        waited += pollInterval;

        if (window.Rebuy && window.Rebuy.SmartCart &&
            typeof window.Rebuy.SmartCart.show === 'function') {
          clearInterval(poll);
          resetAtcBtn(
            customAtcEl && customAtcEl.querySelector('.kv-wps__atc-text'),
            customAtcEl && customAtcEl.querySelector('.kv-wps__atc-loading')
          );
          window.Rebuy.SmartCart.show();
          if (window.posthog) window.posthog.capture('cart_drawer_open', { source: 'rebuy_polled', wait_ms: waited });
          return;
        }

        if (waited >= maxWait) {
          clearInterval(poll);
          resetAtcBtn(
            customAtcEl && customAtcEl.querySelector('.kv-wps__atc-text'),
            customAtcEl && customAtcEl.querySelector('.kv-wps__atc-loading')
          );
          // Rebuy failed — fallback to Dawn cart drawer (items already in cart via AJAX)
          openDawnDrawerFallback();
        }
      }, pollInterval);
    }

    // Fallback: fetch fresh cart HTML and open Dawn's drawer
    function openDawnDrawerFallback() {
      var drawer = document.querySelector('cart-drawer');
      if (!drawer) { window.location.href = '/checkout'; return; }

      // Fetch the updated cart-drawer section so it shows the items we just added
      fetch('/?sections=cart-drawer,cart-icon-bubble')
        .then(function (r) { return r.json(); })
        .then(function (sections) {
          // Update cart drawer HTML with fresh cart contents
          var drawerHTML = new DOMParser()
            .parseFromString(sections['cart-drawer'], 'text/html')
            .querySelector('#CartDrawer');
          var target = document.getElementById('CartDrawer');
          if (drawerHTML && target) target.innerHTML = drawerHTML.innerHTML;

          // Update cart icon bubble count
          var bubbleHTML = new DOMParser()
            .parseFromString(sections['cart-icon-bubble'], 'text/html')
            .querySelector('.shopify-section');
          var bubbleTarget = document.getElementById('cart-icon-bubble');
          if (bubbleHTML && bubbleTarget) bubbleTarget.innerHTML = bubbleHTML.innerHTML;

          // Open the drawer
          drawer.classList.add('animate', 'active');
          document.body.classList.add('overflow-hidden');
          if (window.posthog) window.posthog.capture('cart_drawer_open', { source: 'dawn_fallback' });
        })
        .catch(function () {
          // If even the section fetch fails, go to checkout
          if (window.posthog) window.posthog.capture('cart_drawer_open', { source: 'checkout_redirect' });
          window.location.href = '/checkout';
        });
    }

    // --- Preselect default weight + plan on load (CRO) ----------------------
    // The buy box used to render "Select Weight" with no price (Rs. 0.00) until
    // a band was picked — the single biggest PDP->ATC drop. Pre-pick the most
    // common band (27-45 kg, ~40% of customers) so price + Add to Cart show
    // immediately. We replay the real weight click, then the resolved plan
    // click, so the sticky bar (which syncs off button click events /
    // aria-pressed, not internal state) populates too, independent of which
    // script inits first. applyPlanAvailability shifts the plan up if the
    // default 30-day is out of stock for this band.
    (function preselectDefault() {
      if (root.querySelector('[data-weight-btn][aria-pressed="true"]')) return;
      var defWeight = root.querySelector('[data-weight-btn="27-45"]');
      if (!defWeight) return;
      defWeight.click();
      // A page can start on a different plan through window.KV_WPS_OFFER.defaultPlan.
      // The routine lander prices the 90-day subscription, so it starts there.
      var wanted = offer().defaultPlan ? root.querySelector('[data-plan-btn="' + offer().defaultPlan + '"]') : null;
      var activePlan = (wanted && !wanted.disabled) ? wanted : root.querySelector('[data-plan-btn][aria-pressed="true"]');
      if (activePlan) activePlan.click();
    })();
  }

  // --- Bootstrap ---
  function initAll() {
    document.querySelectorAll('[data-kv-wps]').forEach(function (el) {
      initWeightPlanSelector(el.dataset.kvWps);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function () {
    document.querySelectorAll('[data-kv-wps]').forEach(function (el) {
      delete el.dataset.kvWpsInit;
    });
    initAll();
  });
})();
